
import { useEffect, useContext, useState, useRef } from "react";

import { Form, Input, Button, message, Alert, Select, Divider } from 'antd-v4';

import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import ExternModal from "../../../view/components/extern-modal";

import leedpulse from '../../../configs/leedpulse';

// Auth
import { FirebaseContext } from "../../../context/firebaseContext";
import { useSubscription } from "../../../hooks/useSubscription";

import { BackendContext } from '../../../context/backendContext';
import CustomModal from "../../../view/components/custom-modal/index.jsx";

import { CheckCircleTwoTone, CrownOutlined } from '@ant-design/icons';

// Images
import logoLeedpulse from '../../../assets/images/logo/logo-small-dark.png';

// Moment
import moment from 'moment';
import GatewayConfig from "../profile/gateway-config";

import { updateSubscription } from "../../../services/SubscriptionService";

export default function Payment() {

    const { subscriptionId, userData, getSubscriptionsByClaims, _setSubscription, getAIById, getAIByApiKey, putAI } = useContext(FirebaseContext);
    const { subscription } = useSubscription();

    const [displayPaymentPopup, setDisplayPaymentPopup] = useState(false);
    const [displayInfoPopup, setDisplayInfoPopup] = useState(false);
    const [displaySuccessPopup, setDisplaySuccessPopup] = useState(false);
    const [displayAiPopup, setDisplayAiPopup] = useState(false);
    const [displayWaitingPopup, setDisplayWaitingPopup] = useState(false);
    const [displayGatewayConfigPopup, setDisplayGatewayConfigPopup] = useState(false);
    const [loading, setLoading] = useState(false);

    const [AI, setAI] = useState(null);
    const [wipAI, setWipAI] = useState(null);
    const [subscriptions, setSubscriptions] = useState(null);
    const [activeSubscription, setActiveSubscription] = useState(null);

    const [activationCode, setActivationCode] = useState('');
    const activationCodeRef = useRef();

    const [planStart, setPlanStart] = useState(null);
    const [options, setOptions] = useState(null);
    const { postRequest } = useContext(BackendContext);

    const stripePromise = loadStripe('pk_live_51P8qTjIF5WX4bLlZHMk8BovUhsr3edhG5sBsEuIOtQKIB9fhmaJB5irFIDy6rcjysmmkYD3LpeLShbEDn3hXO3sa00FVs10e5O');

    useEffect(() => {
        if (subscription?.id_ai) {
            loadAI();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscription?.id_ai]);

    useEffect(() => {
        if (userData) {
            getSubscriptionsByClaims().then((data) => {
                if (!data?.doc || !Array.isArray(data.doc) || data.doc.length === 0) return console.log("No subscription found");
                setSubscriptions(data.doc);

                // Loop all subscription to get the active subscription
                data.doc.forEach((subscription) => {
                    if (subscription.id === subscriptionId) {
                        setActiveSubscription(subscription);
                    }
                });
            });
        }
    }, [userData]);

    const loadAI = async () => {
        if (!subscription?.id_ai) return;
        const resAI = await getAIById(subscription.id_ai);
        if (!resAI.doc) return message.error("Une erreur s'est produite lors du chargement de l'IA");
        setAI(resAI.doc);
        setWipAI(resAI.doc);
    };

    useEffect(() => {
        async function handlePaymentPopup() {
            if (subscription && subscription.settings.payment_wall) {
                // Display General terms popup
                setDisplayInfoPopup(true);
            } else if (subscription && !subscription.payment_wall) {
                // After success payment screen
                if (moment(subscription.plan_start).isAfter(new Date().getTime())) {
                    // Display success payment popup
                    setDisplaySuccessPopup(true);
                    setPlanStart(subscription.plan_start);
                } else if (moment(subscription.plan_start).isBefore(new Date().getTime()) && wipAI && wipAI.updated === wipAI.created) {
                    setDisplayAiPopup(true);
                }
            }
        }
        handlePaymentPopup();
    }, [subscription, AI]);

    const getClientSecret = async (sApiKey, activationCode) => {
        return await postRequest("/api/v1/stripe/payment/session", {
            email: userData.email,
            activation_code: activationCode
        }, sApiKey);
    };

    const handleUpdateButton = async () => {
        const oRes = await putAI({ ...wipAI, updated: new Date().getTime() });
        const oSubsRes = await updateSubscription({ ...subscription, name: wipAI.name, updated: new Date().getTime() })

        if (oRes.success && oSubsRes.success) {
            message.success("Informations mises à jour avec succès.");
        } else {
            message.success("Une erreur s'est produite pendant la mise à jour des informations.");
        }
    };

    const isActivationCodeValid = () => {
        return leedpulse.activation_code_payment.includes(activationCode);
    };

    const displayCGVContent = () => {
        return <div style={{ display: 'flex', margin: '2rem', alignItems: 'center', flexDirection: 'column' }}>
            <img src={logoLeedpulse} alt="Leedpulse logo" style={{ width: '200px', height: '200px' }} />
            <p>En continuant, j'accepte les Conditions Générales de Vente de Leedpulse.</p>
            <a target="_blank" rel="noopener noreferrer" href="https://www.leedpulse.fr/cgv-septeo">Voir les CGV</a>
            <Button
                loading={loading}
                onClick={async () => {
                    setLoading(true);
                    // Display Payment popup
                    const oResponse = await getClientSecret(subscription.api_key, activationCode);
                    setDisplayInfoPopup(false);
                    if (oResponse.success && oResponse?.data?.client_secret) {
                        setOptions({
                            clientSecret: oResponse.data.client_secret
                        });
                        // Display payment popup
                        setDisplayPaymentPopup(true);
                    } else {
                        // Display waiting payment popup
                        setDisplayWaitingPopup(true);
                    }
                    setLoading(false)
                }}
                type="primary"
                style={{ marginTop: '1rem' }}>J'accepte</Button>
        </div>
    };

    const displayVerification = () => {
        return (
            <div style={{ display: 'flex', margin: '2rem', alignItems: 'center', flexDirection: 'column' }}>
                {/* Subsription switcher */}
                {activeSubscription && subscriptions && subscriptions.length > 1 && (
                    <Form.Item name={"subscription-name"} label="Vous êtes connecté à">
                        <Select
                            showSearch
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            defaultValue={activeSubscription?.name}
                            onChange={async (value) => {
                                const subscription = subscriptions.find((subscription) => subscription.name === value);
                                setActiveSubscription(subscription);
                                _setSubscription(subscription.id, subscription.api_key);

                                // Local storage of subscription ID & reload page
                                localStorage.setItem("leedpulse-subscription", JSON.stringify(subscription));
                                localStorage.setItem("leedpulse-subscriptionId", subscription.id);

                                // Get AI document from subscription ID
                                const oAI = await getAIByApiKey(subscription.api_key);
                                localStorage.setItem("leedpulse-ai", JSON.stringify(oAI.doc));
                                window.location.reload();
                            }}
                        >
                            {subscriptions && subscriptions.map((subscription) => (
                                <Select.Option key={subscription.id} value={subscription.name}>
                                    {subscription.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                <Divider style={{ borderColor: '#6737ff', fontSize: '1.2rem', fontWeight: 'bold' }}><CrownOutlined /> Votre demande d’activation a bien été prise en compte !</Divider>
                <div style={{ marginTop: '1rem' }}>
                    <p>Un membre de notre équipe va vous contacter très rapidement pour vous présenter Leedpulse et répondre à vos questions. Cette étape est essentielle pour bien démarrer et s’assurer que la solution est parfaitement adaptée à vos besoins.</p>
                    <i>Une fois l’échange terminé, notre conseiller vous transmettra un code de validation à entrer ci-dessous pour finaliser l’activation</i>
                </div>
                <Input status={activationCode !== '' && !isActivationCodeValid() ? 'error' : ''} ref={activationCodeRef} style={{ width: 150, marginTop: '2rem', marginBottom: '1rem' }} placeholder="Code d'activation"></Input>
                {
                    activationCode !== '' && !isActivationCodeValid() && <p style={{ color: 'red' }}>Code incorrect ou expiré. Merci de contacter notre équipe si besoin.</p>
                }
                <Button
                    onClick={() => {
                        const value = activationCodeRef.current.input.value;
                        setActivationCode(value);
                    }}
                    type="primary"
                    style={{ marginTop: '1rem' }}>
                    Valider le code
                </Button>
            </div>
        )
    };

    const handleContentDisplay = () => {
        if (isActivationCodeValid()) {
            return (
                displayCGVContent()
            );
        }
        return (
            displayVerification()
        );
    };

    return (
        <>
            <CustomModal hide={() => { setDisplaySuccessPopup(true) }} visible={displaySuccessPopup} title={'Merci'} icon={<CheckCircleTwoTone style={{ fontSize: 86 }} twoToneColor="#52c41a" />} message={`Un peu de patience, votre compte sera actif à partir du ${moment(planStart).format('DD/MM/YYYY')}`} />
            <ExternModal visible={displayAiPopup} width={'50%'} body={
                <div style={{ display: 'flex', margin: '2rem', flexDirection: 'column' }}>
                    <h4>ℹ️ Ces informations sont-elles correctes ?</h4>
                    <Alert message="Ces informations sont importantes puisqu'elles sont utilisées par l'IA pour répondre à vos clients. Veuillez les vérifier." type="warning" style={{ marginTop: '1rem' }} />
                    <Form style={{ marginTop: '1rem' }} layout="vertical">
                        <Form.Item
                            label="Nom commercial"
                            name="name"
                        >
                            <Input placeholder="Ex: Agence XYZ" defaultValue={wipAI?.name} onChange={(e) => setWipAI({ ...wipAI, name: e.target.value })} />
                        </Form.Item>

                        <Form.Item
                            label="A propos de votre entreprise"
                            name="intro"
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder='Une description longue de la structure et de ses services. (500-1200 caractères recommandés)'
                                defaultValue={wipAI?.intro}
                                onChange={(e) => setWipAI({ ...wipAI, intro: e.target.value })}
                            />
                        </Form.Item>

                        <Form.Item
                            label="Localisation"
                            name="location"
                        >
                            <Input defaultValue={wipAI?.location} onChange={(e) => setWipAI({ ...wipAI, location: e.target.value })} placeholder="Ex: Paris, France" />
                        </Form.Item>

                        <Form.Item
                            label="Site web"
                            name="website"
                        >
                            <Input defaultValue={wipAI?.website} onChange={(e) => setWipAI({ ...wipAI, website: e.target.value })} placeholder="Ex: https://www.exemple.com" />
                        </Form.Item>
                    </Form>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                        <Button
                            onClick={() => {
                                handleUpdateButton();
                                setDisplayAiPopup(false);
                                setDisplayGatewayConfigPopup(subscription.partner_system === "septeo");
                            }}
                            type="primary"
                            style={{ marginTop: '1rem', width: '25%' }}>Valider les informations</Button>
                    </div>
                </div>
            } />
            <ExternModal visible={displayGatewayConfigPopup} width={'50%'} body={
                <div style={{ padding: '1rem' }}>
                    <h3>Configuration de l'IA</h3>
                    <p className="hp-p1-body">
                        Configurez votre intelligence artificielle Leedpulse
                    </p>
                    <GatewayConfig onSubmit={() => setDisplayGatewayConfigPopup(false)} />
                </div>
            } />
            {displayPaymentPopup && <ExternModal maskStyle={{ backgroundColor: 'rgba(45, 52, 54, 1)' }} visible={displayPaymentPopup} width={'100%'} body={
                <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
                    <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
            } />}
            <ExternModal maskStyle={{ backgroundColor: 'rgba(45, 52, 54, 1)' }} visible={displayInfoPopup} width={'50%'} body={
                handleContentDisplay()
            } />
            <ExternModal maskStyle={{ backgroundColor: 'rgba(45, 52, 54, 1)' }} visible={displayWaitingPopup} width={'50%'} body={
                <div style={{ display: 'flex', margin: '2rem', alignItems: 'center', flexDirection: 'column' }}>
                    <img src={logoLeedpulse} alt="Leedpulse logo" style={{ width: '200px', height: '200px' }} />
                    <h4>Oups</h4>
                    <p>Votre compte est en attente d'un paiement.</p>
                    <p>Veuillez vous rapprocher de votre chargé de projet pour réactiver votre accès.</p>
                </div>
            } />
        </>
    );
}
